import { createSelector } from '@reduxjs/toolkit';
import { selectUserId, updateRole } from '@/entities/session';
import { DataStatus, isError, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as UserManagementAPI from '../api/userManagementAPI';
import { selectRoleById } from './rolesSlice';
import type { UserSettingsMeta, User } from './types';

interface State {
  users:
    | {
        data: User[];
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  userSettingsMeta:
    | {
        data: UserSettingsMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
}

const initialState: State = {
  users: {
    data: null,
    status: DataStatus.idle,
  },
  userSettingsMeta: {
    data: null,
    status: DataStatus.idle,
  },
};

export const userManagementSlice = createAppSlice({
  name: 'userManagement',
  initialState,
  reducers: (create) => ({
    setupUsers: create.asyncThunk(
      (_: void) => {
        return UserManagementAPI.getOrganizationUsers();
      },
      {
        pending: (state) => {
          state.users.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.users.data = action.payload;
          state.users.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.users.status = DataStatus.error;
        },
      },
    ),
    inviteUser: create.asyncThunk<{ email: string; roleId: string }, User>(
      ({ email, roleId }) => {
        return UserManagementAPI.inviteUserToOrganization(email, roleId);
      },
      {
        pending: (state) => {
          state.users.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (!state.users.data) {
            return;
          }

          state.users.data.push(action.payload);
          state.users.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.users.status = DataStatus.finished;
        },
      },
    ),
    updateUser: create.asyncThunk<
      {
        user: User;
        update: {
          roleId?: string;
          isActive?: boolean;
        };
        silent?: boolean;
      },
      User
    >(
      ({ user, update }, { getState, dispatch }) => {
        const state = getState() as RootState;
        const currentUserId = selectUserId(state);
        const updatedUser = {
          id: user.id,
          ...(update.roleId ? { roleId: update.roleId } : {}),
          ...(update.isActive != null ? { isActive: update.isActive } : {}),
        };

        return UserManagementAPI.updateUser(updatedUser).then((user) => {
          if (user.id === currentUserId && update.roleId) {
            const role = selectRoleById(state, user.roleId);
            if (role) {
              dispatch(updateRole({ id: role.id, name: role.name, rules: role.rules }));
            }
          }

          return user;
        });
      },
      {
        pending: (state, action) => {
          if (action.meta.arg.silent) {
            return;
          }

          state.users.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (!state.users.data) {
            return;
          }

          state.users.data = state.users.data.map((user) => {
            if (user.id === action.payload.id) {
              return {
                ...user,
                ...action.payload,
              };
            }
            return user;
          });

          if (!action.meta.arg.silent) {
            state.users.status = DataStatus.finished;
          }
        },
        rejected: (state, action) => {
          if (action.meta.arg.silent) {
            return;
          }

          state.users.status = DataStatus.finished;
        },
      },
    ),
    batchUpdateUsers: create.asyncThunk<
      {
        updates: Array<{
          user: User;
          update: { roleId?: string; isActive?: boolean };
        }>;
      },
      User[]
    >(
      ({ updates }, { getState, dispatch }) => {
        const state = getState() as RootState;
        const currentUserId = selectUserId(state);

        const payload = updates.map(({ user, update }) => ({
          userId: user.id,
          ...(update.roleId ? { roleId: update.roleId } : {}),
          ...(update.isActive != null ? { isActive: update.isActive } : {}),
        }));

        return UserManagementAPI.updateUsersBatch(payload).then((users) => {
          const currentUserResult = users.find((u) => u.id === currentUserId);
          if (currentUserResult) {
            const currentUserUpdate = updates.find(
              ({ user }) => user.id === currentUserId,
            )?.update;
            if (currentUserUpdate?.roleId) {
              const role = selectRoleById(state, currentUserResult.roleId);
              if (role) {
                dispatch(
                  updateRole({ id: role.id, name: role.name, rules: role.rules }),
                );
              }
            }
          }
          return users;
        });
      },
      {
        fulfilled: (state, action) => {
          if (!state.users.data) return;
          const updatedMap = new Map(action.payload.map((u) => [u.id, u]));
          state.users.data = state.users.data.map((user) => {
            const updated = updatedMap.get(user.id);
            return updated ? { ...user, ...updated } : user;
          });
        },
      },
    ),
    resendInvite: create.asyncThunk<string, void>(
      (email) => {
        return UserManagementAPI.resendInvite(email);
      },
      {
        pending: (state) => {
          state.users.status = DataStatus.loading;
        },
        fulfilled: (state) => {
          state.users.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.users.status = DataStatus.finished;
        },
      },
    ),
    setupUserSettingsMeta: create.asyncThunk(
      (_: void) => {
        return UserManagementAPI.getUserSettingsMeta();
      },
      {
        pending: (state) => {
          state.userSettingsMeta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.userSettingsMeta.data = action.payload;
          state.userSettingsMeta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.userSettingsMeta.status = DataStatus.error;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (isLoading(state.userManagement.userSettingsMeta.status)) {
              return false;
            }
          },
        },
      },
    ),
  }),
  selectors: {
    selectUsers: (state) => {
      return state.users.data;
    },
    selectIsUsersLoading: (state) => {
      return isLoading(state.users.status);
    },
    selectIsUsersFailed: (state) => {
      return isError(state.users.status);
    },
    selectUserSettingsMeta: (state) => {
      return state.userSettingsMeta.data;
    },
    selectIsUserSettingsMetaLoading: (state) => {
      return isLoading(state.userSettingsMeta.status);
    },
    selectIsUserSettingsMetaFailed: (state) => {
      return isError(state.userSettingsMeta.status);
    },
    selectUserById: createSelector(
      (state) => state.users.data,
      (_, userId: string) => {
        return userId;
      },
      (users: User[], userId: string) => {
        return users?.find((user: User) => user.id === userId);
      },
    ),
  },
});

export const {
  inviteUser,
  setupUsers,
  updateUser,
  batchUpdateUsers,
  setupUserSettingsMeta,
  resendInvite,
} = userManagementSlice.actions;
export const {
  selectUsers,
  selectIsUsersLoading,
  selectIsUsersFailed,
  selectUserSettingsMeta,
  selectIsUserSettingsMetaLoading,
  selectIsUserSettingsMetaFailed,
  selectUserById,
} = userManagementSlice.selectors;
